/**
 * VendingContractSignPage — v2.5.912
 *
 * PUBLIC route (no auth). A contract signatory opens the token URL, reviews
 * the contract HTML in an iframe, types their name, checks the "I agree" box,
 * and submits. Backend stamps the typed_signature + timestamp on the
 * signatories array. If everyone has signed, the contract auto-activates.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, FileSignature, XCircle } from 'lucide-react';

// Bare axios instance — no bearer token needed for public sign endpoints.
const publicAxios = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });

export default function VendingContractSignPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: '', data: null, submitted: false });
  const [typedSig, setTypedSig] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    publicAxios.get(`/api/vending/enterprise/contracts/sign/${token}`)
      .then(res => { if (alive) setState({ loading: false, error: '', data: res.data, submitted: !!res.data?.signatory?.signed_at }); })
      .catch(err => { if (alive) setState({ loading: false, error: err.response?.data?.detail || 'Sign link is invalid or expired.', data: null, submitted: false }); });
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (!typedSig.trim()) { alert('Please type your full legal name to sign.'); return; }
    if (!agree) { alert('You must acknowledge the agreement to sign.'); return; }
    setBusy(true);
    try {
      await publicAxios.post(`/api/vending/enterprise/contracts/sign/${token}`, {
        typed_signature: typedSig.trim(),
        agree: true,
      });
      setState(s => ({ ...s, submitted: true }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not submit signature. Please try again.');
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <XCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
            <div className="text-lg font-semibold mb-1">Invalid sign link</div>
            <div className="text-sm text-gray-600">{state.error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { contract, signatory, client_name } = state.data;

  if (state.submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <div className="text-xl font-bold mb-1">Contract signed</div>
            <div className="text-sm text-gray-600 mb-3">
              Thank you, {signatory.full_name || 'signatory'}. Your signature on{' '}
              <b>{contract.contract_number || contract.title}</b> has been recorded.
            </div>
            <div className="text-[11px] text-gray-400">You can close this window.</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-3 sm:p-6" data-testid="contract-sign-page">
      <div className="max-w-4xl mx-auto space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileSignature className="h-5 w-5 text-emerald-600" />
              <div className="text-lg sm:text-xl font-bold">{contract.title || 'Vending Services Agreement'}</div>
            </div>
            <div className="text-xs text-gray-600 flex flex-wrap gap-2">
              <span>Contract # <b>{contract.contract_number}</b></span>
              <span>Client: <b>{client_name}</b></span>
              <span>Effective <b>{contract.starts_on}</b> — <b>{contract.ends_on || 'ongoing'}</b></span>
            </div>
          </CardContent>
        </Card>

        {contract.generated_document_html ? (
          <div className="border rounded overflow-hidden bg-white">
            <iframe title="Contract document" srcDoc={contract.generated_document_html} className="w-full h-[70vh]" />
          </div>
        ) : (
          <Card><CardContent className="p-4 text-center text-sm text-gray-500">
            The operator has not generated a printable document for this contract yet. Please contact them to send you the printable version before signing.
          </CardContent></Card>
        )}

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="text-sm">
              <b>Signing as:</b> {signatory.full_name || <em className="text-gray-400">unnamed</em>} · {signatory.role} · <span className="text-gray-500">{signatory.email}</span>
            </div>
            <Label className="text-[11px] uppercase tracking-wide text-gray-600">Type your full legal name</Label>
            <Input
              value={typedSig}
              onChange={e => setTypedSig(e.target.value)}
              placeholder="Firstname Lastname"
              className="text-lg font-serif italic h-11"
              data-testid="typed-signature-input"
            />
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-0.5" data-testid="agree-checkbox" />
              <span>
                I acknowledge I have read the agreement above and am authorised to sign on behalf of the party I represent.
                My typed signature constitutes a legally-binding electronic signature.
              </span>
            </label>
            <Button
              onClick={submit}
              disabled={busy || !typedSig.trim() || !agree}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
              data-testid="submit-signature-btn"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing…</> : 'Sign this contract'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
